import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { publicApi, billingApi, clusterApi } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import { parseServerDate } from "../utils/formatters";
import "./SignUpSuccessPage.css";

type VerificationState = "loading" | "success" | "error";

const SignUpSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  
  const [state, setState] = useState<VerificationState>("loading");
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [isTrialing, setIsTrialing] = useState(false);
  const [trialEndDate, setTrialEndDate] = useState<string | null>(null);
  
  // Prevent multiple verification attempts
  const verificationAttempted = useRef(false);
  
  // Get session_id from URL
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const verifySignup = async () => {
      if (!sessionId) {
        setState("error");
        setError("Invalid verification link. No session ID provided.");
        return;
      }

      // Prevent duplicate calls (React StrictMode)
      if (verificationAttempted.current) return;
      verificationAttempted.current = true;

      try {
        const response = await publicApi.verifySignup(sessionId);
        
        // Store the token and trigger auth context update
        if (response.access_token) {
          loginWithToken(response.access_token);
          
          // Check billing status for trial info
          try {
            const status = await billingApi.getStatus();
            if (status.is_trialing || status.subscription_status === "trialing") {
              setIsTrialing(true);
              if (status.trial_end) {
                setTrialEndDate(parseServerDate(status.trial_end).toLocaleDateString());
              }
            }
          } catch {
            // Non-critical — proceed without trial info
          }
        }
        
        setState("success");
      } catch (err) {
        setState("error");
        setError(
          err instanceof Error 
            ? err.message 
            : "Failed to verify signup. Please try logging in manually."
        );
      }
    };

    verifySignup();
  }, [sessionId, loginWithToken]);

  // Navigate to the first available client/domain
  const navigateToFirstClient = useCallback(async () => {
    try {
      const clients = await clusterApi.getClients();
      if (clients && clients.length > 0) {
        navigate(`/${clients[0].id}`);
      } else {
        navigate("/default");
      }
    } catch {
      navigate("/default");
    }
  }, [navigate]);

  // Countdown and redirect on success
  useEffect(() => {
    if (state !== "success") return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigateToFirstClient();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [state, navigate, navigateToFirstClient]);

  const handleContinue = () => {
    navigateToFirstClient();
  };

  const handleLogin = () => {
    navigate("/login");
  };

  return (
    <div className="success-page">
      <div className="success-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="success-container">
        {/* Header */}
        <div className="success-header">
          <div className="success-logo">
            <img src="/clusterview-logo.svg" alt="ClusterView" className="logo-icon" style={{ width: 32, height: 32 }} />
          </div>
          <h1>ClusterView</h1>
        </div>

        {/* Loading State */}
        {state === "loading" && (
          <div className="success-card">
            <div className="success-loading">
              <div className="success-spinner"></div>
              <h2>Setting up your account...</h2>
              <p>Please wait while we verify and configure your account.</p>
            </div>
          </div>
        )}

        {/* Success State */}
        {state === "success" && (
          <div className="success-card success-card-success">
            <div className="success-icon">
              <CheckCircleIcon style={{ fontSize: 64, color: "#10b981" }} />
            </div>
            <h2>Welcome to ClusterView!</h2>
            {isTrialing ? (
              <p>
                Your free trial has started!
                {trialEndDate
                  ? ` You won't be charged until ${trialEndDate}.`
                  : " You won't be charged during the trial period."}
              </p>
            ) : (
              <p>Your account has been created and your subscription is now active.</p>
            )}
            
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "14px 18px", marginBottom: 20, textAlign: "left" }}>
              <p style={{ color: "#166534", fontSize: 14, margin: 0, lineHeight: 1.5 }}>
                <strong>Check your inbox:</strong> We've sent a verification email to confirm your address. Click the link in the email to verify.
              </p>
            </div>

            <div className="success-features">
              <h3>You now have access to:</h3>
              <ul>
                <li>Keyword clustering and analysis</li>
                <li>SERP preview and tracking</li>
                <li>Topic suggestions</li>
                <li>Team collaboration</li>
              </ul>
            </div>

            <button className="success-btn" onClick={handleContinue}>
              Go to Dashboard
            </button>
            
            <p className="success-redirect">
              Redirecting in {countdown} seconds...
            </p>
          </div>
        )}

        {/* Error State */}
        {state === "error" && (
          <div className="success-card success-card-error">
            <div className="success-icon">
              <WarningIcon style={{ fontSize: 64, color: "#f87171" }} />
            </div>
            <h2>Verification Issue</h2>
            <p className="error-message">{error}</p>
            
            <div className="success-actions">
              <button className="success-btn" onClick={handleLogin}>
                Go to Login
              </button>
              <p className="success-hint">
                If payment was successful, try logging in with your credentials.
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="success-footer">
          <p>
            Need help? <Link to="/login">Contact Support</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUpSuccessPage;
